import { useCallback, useEffect, useState } from 'react';
import ElectronHelper from 'src/common/electron/ElectronHelper';
import { RetailElectronAPI } from 'src/common/electron/RetailElectronAPI';
import { registerMainWindowListener } from 'src/main-window-listener';

export const CUSTOMER_DISPLAY_CHANNEL = 'customer-display-channel';
export enum CustomerDisplayLifecycle {
  Loaded = 'CUSTOMER_DISPLAY_LOADED',
}

export default function useCustomerDisplay<t>(
  showDisplay: boolean,
  customerDisplayNumber?: number,
): [MessagePort, (state: t) => void] {
  const [channel] = useState<MessageChannel>(new MessageChannel());
  // for managing the customer display lifecycle
  useEffect(() => {
    let closeDisplayFn = Promise.resolve((): void => {});

    if (showDisplay && ElectronHelper.isElectron()) {
      registerMainWindowListener(channel.port2);
      closeDisplayFn = openElectronCustomerDisplay(customerDisplayNumber);
    } else if (showDisplay) {
      closeDisplayFn = openWebBrowserCustomerDisplay(channel.port2);
    }

    return () => {
      closeDisplayFn.then(close => close());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDisplay]);

  return [channel.port1, useCallback((state: t) => channel.port1.postMessage(state), [channel])];
}

async function openElectronCustomerDisplay(customerDisplayNumber?: number): Promise<() => void> {
  const custDisplayController = RetailElectronAPI.getCustomerDisplayController();
  custDisplayController.startCustomerDisplay(customerDisplayNumber);

  return () => custDisplayController.closeCustomerDisplay();
}

async function openWebBrowserCustomerDisplay(port: MessagePort): Promise<() => void> {
  const origin = window.location.origin;
  const urlQueryParams = new URLSearchParams({
    customerDisplay: 'true',
  }).toString();

  const customerDisplayRef = window.open(
    `${origin}?${urlQueryParams}`,
    'Customer Display',
    'width=1000,height=1000',
  );

  const CustDisplayLoadedListener = (event): void => {
    if (event.data === CustomerDisplayLifecycle.Loaded) {
      customerDisplayRef?.postMessage(CUSTOMER_DISPLAY_CHANNEL, origin, [port]);
      window.removeEventListener('message', CustDisplayLoadedListener);
    }
  };
  window.addEventListener('message', CustDisplayLoadedListener, false);
  return () => customerDisplayRef?.close();
}
